/**
 * Format: Variant
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API variants response.
 *
 */
import { formatImage } from '~/format/format-image'
import { formatMoneyV2 } from '~/format/format-price'
import { formatUrl } from '~/format/format-product'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Format product variants array.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/ProductVariant
 * @param {Object} product - Product response.
 * @param {Object} [collection] - Formatted collection object.
 * @returns {Array}
 */
export function formatVariants(product, collection) {
  return product?.variants
    ? transformEdges(product.variants).map((variant) => {
      return formatVariant(variant, product, collection)
    })
    : []
}

/**
 * Format product variant.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/ProductVariant
 * @param {Object} variant - Variant response.
 * @param {Object} product - Product response.
 * @param {Object} [collection] - Formatted collection object.
 * @returns {Object}
 */
function formatVariant(variant, product, collection) {
  const formattedVariant = {
    available: variant.available,
    compareAtPrice: formatMoneyV2(variant.compareAtPrice),
    id: decodeApiId(variant.id),
    image: formatImage(variant.image),
    inventory: variant.quantityAvailable,
    price: formatMoneyV2(variant.price),
    selectedOptions: variant.selectedOptions.map((option) => {
      return {
        name: option.name,
        value: option.value,
      }
    }),
    sku: variant.sku,
    title: variant.title,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const url = collection
    ? `${cnvs.routes.normalisedRoot}collections/${collection.handle}/products/${product.handle}?variant=${formattedVariant.id}`
    : `${cnvs.routes.normalisedRoot}products/${product.handle}?variant=${formattedVariant.id}`

  formattedVariant.url = formatUrl(url, product.trackingParameters)

  return formattedVariant
}
